import flatpickr from 'flatpickr'
import type { Instance as FlatpickrInstance } from 'flatpickr/dist/types/instance'
import { useEffect, useRef } from 'react'
import { useController, type Control, type FieldPath, type FieldValues } from 'react-hook-form'

interface FormDatePickerProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>
  name: FieldPath<TFieldValues>
  label?: string
  placeholder?: string
  minDate?: string
  required?: boolean
}

function toIsoDate(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function FormDatePicker<TFieldValues extends FieldValues>({
  control,
  name,
  label,
  placeholder = 'Select date',
  minDate,
  required,
}: FormDatePickerProps<TFieldValues>) {
  const {
    field,
    fieldState: { error },
  } = useController({ control, name })
  const inputRef = useRef<HTMLInputElement>(null)
  const fieldRef = useRef(field)
  const instanceRef = useRef<FlatpickrInstance | null>(null)

  useEffect(() => {
    fieldRef.current = field
  })

  useEffect(() => {
    const el = inputRef.current
    if (!el) return

    const instance = flatpickr(el, {
      dateFormat: 'Y-m-d',
      minDate,
      defaultDate: fieldRef.current.value || undefined,
      onChange: ([date]) => {
        fieldRef.current.onChange(date ? toIsoDate(date) : null)
      },
    })
    instanceRef.current = instance

    return () => {
      instance.destroy()
      instanceRef.current = null
    }
  }, [minDate])

  // Keep the picker in sync when the value changes from outside (e.g. an
  // edit form's reset() after its data loads), not only on first mount.
  useEffect(() => {
    const instance = instanceRef.current
    if (!instance) return
    const current = instance.selectedDates[0] ? toIsoDate(instance.selectedDates[0]) : ''
    const next = (field.value as string | null | undefined) ?? ''
    if (current !== next) {
      instance.setDate(next || [], false)
    }
  }, [field.value])

  return (
    <div className="mb-3">
      {label && (
        <label htmlFor={name} className="form-label">
          {label}
          {required && <span className="text-danger ms-1">*</span>}
        </label>
      )}
      <input
        ref={inputRef}
        id={name}
        type="text"
        placeholder={placeholder}
        readOnly
        className={`form-control ${error ? 'is-invalid' : ''}`.trim()}
        onBlur={field.onBlur}
      />
      {error && <div className="invalid-feedback d-block">{error.message}</div>}
    </div>
  )
}
